import {
  FormikInput,
  Link,
  Stack,
  Typography,
  Alert,
  useResponsive,
  useTheme,
  LoadingButton,
} from '@rabex-kit/rabex-ui';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as yup from 'yup';
import { InfoCircle } from 'iconsax-react';
import useInAppLink from '@/hooks/useInAppLink';
import PasswordChecklist from '../PasswordChecklist';
import { isValidPassword } from '@/utils/passwordRules';
import Arrow from '../../static/images/svg/Controls.svg';

import { LOGIN } from '../../pages/routes';
import { ClearButton, PasswordToggle } from '../AuthIconButton';

export type ForgetProps = {
  isLoading: boolean;
  onSubmit: (values: {
    new_password1: string | number;
    new_password2: string | number;
  }) => void;
};

const ForgetForm: React.FC<ForgetProps> = ({ onSubmit, isLoading }) => {
  const inAppLink = useInAppLink();
  const { matches } = useResponsive();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = yup.object({
    new_password1: yup
      .string()
      .required('رمز عبور الزامی است.')
      // The checklist shows these same rules (utils/passwordRules).
      .test('valid-password', 'لطفاً یک رمز عبور معتبر وارد کنید.', (value) =>
        isValidPassword(value ?? '')
      ),
    new_password2: yup
      .string()
      .oneOf([yup.ref('new_password1'), null], 'رمز عبور مطابقت ندارد')
      .required('رمز عبور الزامی است.')
  });

  return (
    <Stack
      gap={0}
      width={{ xs: '100%', lg: 464 }}
      mt={{ lg: 5, xs: 0 }}
      zIndex={2}>
      <Link
        mb={3}
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        {...inAppLink(LOGIN)}
        fontWeight="600"
        color={theme.palette.textColor[900]}
        sx={{ textDecoration: 'none' }}
        variant={matches({ xs: 'body3', lg: 'body2' })}>
        <Stack direction="row" gap={0} alignItems="center">
          <img src={Arrow} alt="" style={{ width: 24, height: 24 }} />
          {/*  <Typography variant="body2" weight="bold">
            انصراف
          </Typography> */}
        </Stack>
      </Link>
      <Typography
        mb={{ lg: 6, xs: 2 }}
        variant={matches({ lg: 'subtitle', xs: 'body1' })}
        weight="bold">
        تعیین رمز عبور
      </Typography>
      <Alert
        variant="filled"
        color="warning"
        icon={
          <InfoCircle
            size="24"
            color={theme.palette.warning[400]}
            variant="Bold"
          />
        }
        sx={{ mb: matches({ xs: 3, lg: 6 }) }}>
        <Typography
          variant={matches({ xs: 'body3', lg: 'body2' })}
          weight="bold">
          کاربر گرامی برای حفظ امنیت، تا ۲۴ساعت بعد از تغییر رمز، امکان برداشت
          از حساب مقدور نمی‌باشد.
        </Typography>
      </Alert>
      <Formik
        onSubmit={(val) => onSubmit(val)}
        validationSchema={validationSchema}
        initialValues={{
          new_password1: '',
          new_password2: ''
        }}>
        {(formik) => (
          <Form>
            <Stack gap={4}>
              <FormikInput
                placeholder="رمزعبور جدید"
                name="new_password1"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <>
                    {formik.values.new_password1 && (
                      <ClearButton
                        onClick={() =>
                          formik.setFieldValue('new_password1', '')
                        }
                      />
                    )}
                    <PasswordToggle
                      shown={showPassword}
                      onToggle={() => setShowPassword(!showPassword)}
                    />
                  </>
                }
              />
              <FormikInput
                placeholder="تکرار رمزعبور جدید"
                name="new_password2"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <>
                    {formik.values.new_password2 && (
                      <ClearButton
                        onClick={() =>
                          formik.setFieldValue('new_password2', '')
                        }
                      />
                    )}
                    <PasswordToggle
                      shown={showPassword}
                      onToggle={() => setShowPassword(!showPassword)}
                    />
                  </>
                }
              />
              <PasswordChecklist
                password={String(formik.values.new_password1 ?? '')}
              />
              <LoadingButton
                variant="contained"
                fullWidth
                loading={isLoading}
                type="submit"
                disabled={
                  !formik.values.new_password1 || !formik.values.new_password2
                }>
                ذخیره
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default ForgetForm;
